import React, { useState, useEffect } from 'react';
import {
  LayoutDashboard, Store, Ticket, Users, LogOut, Menu,
  Settings, FileSignature, BarChart3, Banknote, Tag, CreditCard, Shield, Compass, Crown, ScrollText, Building2
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import DashboardTab from './admin/DashboardTab';
import StoresTab from './admin/StoresTab';
import StoreDetailView from './admin/StoreDetailView';
import VoucherTypesTab from './admin/VoucherTypesTab';
import VouchersTab from './admin/VouchersTab';
import VoucherDetailView from './admin/VoucherDetailView';
import UsersTab from './admin/UsersTab';
import MembershipTab from './admin/MembershipTab';
import SettingsTab from './admin/SettingsTab';
import AgreementsTab from './admin/AgreementsTab';
import SecurityDashboardTab from './admin/SecurityDashboardTab';
import SettlementsTab from './admin/SettlementsTab';
import ReportsTab from './admin/ReportsTab';
import OnboardingTab from './admin/OnboardingTab';
import PlatinumTab from './admin/PlatinumTab';
import AuditLogTab from './admin/AuditLogTab';
import MallsTab from './admin/MallsTab';

// Maps icon name strings (from DB) to Lucide components.
// Add an entry here when adding a new icon to the menu_items table.
const ICON_REGISTRY = {
  LayoutDashboard, Store, Ticket, Users, Settings,
  FileSignature, BarChart3, Banknote, Tag, CreditCard,
  Shield, Compass, Crown, ScrollText, Building2,
};

// Maps item_key (from DB) to the tab component.
// Add an entry here when building a new tab page.
const TAB_COMPONENTS = {
  dashboard:        DashboardTab,
  vouchers:         VouchersTab,
  stores:           StoresTab,
  'voucher-types':  VoucherTypesTab,
  users:            UsersTab,
  settlements:      SettlementsTab,
  reports:          ReportsTab,
  onboarding:       OnboardingTab,
  security:         SecurityDashboardTab,
  membership:       MembershipTab,
  platinum:         PlatinumTab,
  'audit-log':      AuditLogTab,
  agreements:       AgreementsTab,
  malls:            MallsTab,
  'system-settings': SettingsTab,
};

export default function AdminPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [voucherDetailId, setVoucherDetailId] = useState(null);
  const [storeDetailId, setStoreDetailId] = useState(null);
  const [voucherFilters, setVoucherFilters] = useState({});
  const [navMenuItems, setNavMenuItems] = useState(null); // null = loading
  const [menuLoadError, setMenuLoadError] = useState(false);

  const loadMenuItems = () => {
    setMenuLoadError(false);
    api.get('/api/config/admin/menu-items')
      .then(res => setNavMenuItems(res.data))
      .catch(() => { setNavMenuItems([]); setMenuLoadError(true); });
  };

  useEffect(() => { loadMenuItems(); }, []);

  const handleModulesChanged = () => loadMenuItems();

  // Map API response to {id, label, icon (component)}
  const navItems = (navMenuItems ?? []).map(item => ({
    id:    item.itemKey,
    label: item.label,
    icon:  ICON_REGISTRY[item.icon] ?? LayoutDashboard,
  }));

  const handleLogout = () => { logout(); navigate('/'); };

  const handleNavigate = (tabId, filters) => {
    setTab(tabId);
    setVoucherDetailId(null);
    setStoreDetailId(null);
    if (filters) setVoucherFilters(filters);
    else setVoucherFilters({});
  };

  const handleViewVoucherDetail = (id) => { setVoucherDetailId(id); };
  const handleBackToVouchers   = () => { setVoucherDetailId(null); };
  const handleViewStoreDetail  = (id) => { setStoreDetailId(id); };
  const handleBackToStores     = () => { setStoreDetailId(null); };

  const currentLabel = voucherDetailId ? 'Voucher Detail'
    : storeDetailId ? 'Store Detail'
    : navItems.find(n => n.id === tab)?.label || 'Admin';

  const renderTabContent = () => {
    if (tab === 'vouchers' && voucherDetailId) {
      return <VoucherDetailView voucherId={voucherDetailId} onBack={handleBackToVouchers} />;
    }
    if (tab === 'vouchers') {
      return <VouchersTab onViewDetail={handleViewVoucherDetail} initialFilters={voucherFilters} />;
    }
    if (tab === 'stores' && storeDetailId) {
      return <StoreDetailView storeId={storeDetailId} onBack={handleBackToStores} />;
    }
    if (tab === 'stores') {
      return <StoresTab onViewDetail={handleViewStoreDetail} />;
    }
    if (tab === 'dashboard') {
      return <DashboardTab onNavigate={handleNavigate} />;
    }
    if (tab === 'system-settings') {
      return <SettingsTab onModulesChanged={handleModulesChanged} />;
    }
    const ActiveTab = TAB_COMPONENTS[tab];
    return ActiveTab ? <ActiveTab /> : null;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex" style={{ fontFamily: 'Roboto, sans-serif' }} data-testid="admin-page">
      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-40 w-64 bg-[#561D1A] text-white flex flex-col transition-transform duration-300 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="p-5 border-b border-white/10">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#EC1A23] rounded-lg flex items-center justify-center">
              <Ticket size={18} className="text-white" />
            </div>
            <div>
              <p className="font-heading font-bold text-base">LuxeMall</p>
              <p className="text-white/50 text-xs">Admin Portal</p>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {navMenuItems === null ? (
            <p className="text-white/40 text-xs px-4 py-2">Loading...</p>
          ) : menuLoadError ? (
            <div className="px-4 py-3">
              <p className="text-white/40 text-xs mb-2">Could not load menu.</p>
              <button onClick={loadMenuItems} className="text-white/60 hover:text-white text-xs underline">Retry</button>
            </div>
          ) : navItems.length === 0 ? (
            <p className="text-white/30 text-xs px-4 py-2">No menu items configured.</p>
          ) : (
            navItems.map(({ id, label, icon: Icon }) => (
              <button
                key={id}
                onClick={() => { handleNavigate(id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium transition-all ${
                  tab === id ? 'bg-white/20 text-white' : 'text-white/60 hover:bg-white/10 hover:text-white'
                }`}
                data-testid={`admin-nav-${id}`}
              >
                <Icon size={17} />
                {label}
              </button>
            ))
          )}
        </nav>
        <div className="p-4 border-t border-white/10">
          <div className="flex items-center gap-3 mb-3 px-2">
            <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-white/50 text-xs capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-2 px-4 py-2 rounded-xl text-white/60 hover:text-white hover:bg-white/10 text-sm transition-colors"
            data-testid="admin-logout"
          >
            <LogOut size={15} /> Logout
          </button>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && <div className="fixed inset-0 z-30 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">
        {/* Top bar */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button className="lg:hidden text-gray-500" onClick={() => setSidebarOpen(true)}>
              <Menu size={22} />
            </button>
            <h1 className="font-heading text-xl font-bold text-[#561D1A]" data-testid="page-title">
              {currentLabel}
            </h1>
          </div>
          <div className="hidden md:flex items-center gap-3">
            <div className="text-right">
              <p className="text-sm font-medium text-gray-700">{user?.name}</p>
              <p className="text-xs text-gray-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
            <div className="w-9 h-9 rounded-full bg-[#561D1A] text-white flex items-center justify-center text-sm font-bold">
              {user?.name?.[0]?.toUpperCase()}
            </div>
          </div>
        </header>

        {/* Tab content */}
        <main className="flex-1 p-6">
          {renderTabContent()}
        </main>
      </div>
    </div>
  );
}
